/**
 * Servicio principal de autenticación
 * Maneja el estado de autenticación, tokens y operaciones relacionadas
 */
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, from, of, throwError } from 'rxjs';
import { catchError, map, tap, switchMap, finalize } from 'rxjs/operators';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';

import { IAuthState, IAuthTokens, IUser, UserType } from '../models/auth.types';
import {
  clearAuthData, 
  getAccessToken, 
  getAuthTokens, 
  getUserData, 
  hasAuthTokens,
  saveAuthTokens, 
  saveUserData,
  saveProfileToken,
  getProfileToken
} from '../utils/session-storage.util';
import { environment } from 'src/environments/environment';
import { FirebaseAuthService } from './firebase-auth.service';
import { SessionService } from './session.service';
import { AuthCredential, User } from 'firebase/auth';
import { AuthApiService } from 'src/app/modules/auth/services/auth-api.service';
import { ApiResponse, RegisterRequest, RegisterResponse, ValidateRegisterRequest, AuthSessionRequest, AuthSessionResponse } from '../../models/auth.model';
import { Profile } from '../../models/profile.model';
import { SessionUser } from '../models/session-user.model';

export interface RegistrationPayload {
  email: string;
  password?: string;
  nombres: string;
  apellidos: string;
  phoneNumber: string;
  tipoDocumento: string;
  documentoIdentidad: string;
  userType: UserType;
  selectedPlan?: string;
  colegiaturaNumero?: string;
  colegiaturaRegion?: string;
  avatarUrl?: string;
  metadataJson?: string;
  provider?: string;
  especialidades?: string[];
  direccion?: {
    ciudad: string;
    departamento: string;
    direccion: string;
    codigoPostal?: string;
    latitud?: number;
    longitud?: number;
  };
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  // API base URL
  private apiUrl = `${environment.apiUrl}/auth`;
  
  // Estado inicial de autenticación
  private initialState: IAuthState = {
    user: null,
    tokens: null,
    isAuthenticated: false,
    isLoading: false,
    error: null
  };

  // BehaviorSubject para el estado de autenticación
  private authStateSubject = new BehaviorSubject<IAuthState>(this.initialState);
  
  // Observable público para el estado de autenticación
  public authState$ = this.authStateSubject.asObservable();
  
  // Observables derivados para componentes específicos
  public isAuthenticated$ = this.authState$.pipe(map(state => state.isAuthenticated));
  public user$ = this.authState$.pipe(map(state => state.user));
  public isLoading$ = this.authState$.pipe(map(state => state.isLoading));
  public error$ = this.authState$.pipe(map(state => state.error));

  // URL a la que redirigir después del login
  private redirectUrl: string | null = null;

  private skipNextFirebaseAuthSync = false;
  private pendingGoogleCredential: AuthCredential | null = null;
  private pendingGoogleUser: IUser | null = null;

  private firebaseAuthService = inject(FirebaseAuthService);
  private sessionService = inject(SessionService);

  constructor(
    private http: HttpClient,
    private router: Router,
    private authApiService: AuthApiService
  ) {
    this.checkAuthStatus();
    this.setupFirebaseAuthListener();
    this.setupSessionSync();
  }

  /**
   * Verifica el estado de autenticación al iniciar el servicio
   */
  private checkAuthStatus(): void {
    // Verificar Firebase primero
    const firebaseUser = this.firebaseAuthService.getCurrentUser();
    if (firebaseUser) {
      this.loadFirebaseUser(firebaseUser);
      return;
    }

    // Restauración inicial desde storage para mantener sesión tras recarga.
    // La validación fuerte se realiza en setupFirebaseAuthListener():
    // si Firebase reporta sesión inválida, se fuerza logout.
    if (hasAuthTokens()) {
      const tokens = getAuthTokens();
      const user = getUserData();

      if (tokens && user) {
        this.authStateSubject.next({
          ...this.authStateSubject.value,
          tokens,
          user,
          isAuthenticated: true,
          isLoading: false,
          error: null
        });
        this.syncWithSessionService(user as IUser);
      }
    }
  }

  /**
   * Configura el listener de cambios en el estado de autenticación de Firebase
   */
  private setupFirebaseAuthListener(): void {
    this.firebaseAuthService.authState$.subscribe((firebaseUser) => {
      if (firebaseUser) {
        if (this.skipNextFirebaseAuthSync) {
          return;
        }
        // Solo cargar si no hay un usuario autenticado ya
        if (!this.authStateSubject.value.isAuthenticated) {
          this.loadFirebaseUser(firebaseUser);
        }
      } else {
        // Seguridad: si se pierde la sesión Firebase, invalidar sesión local de app
        // para evitar estado inconsistente (app autenticada con Firebase expirado).
        if (this.authStateSubject.value.isAuthenticated) {
          this.handleLogout();
        }
      }
    });
  }

  /**
   * Carga los datos del usuario de Firebase
   */
  private loadFirebaseUser(firebaseUser: any): void {
    this.completeLoginFlow(firebaseUser as User).subscribe({
      next: ({ user, tokens }) => {
        this.handleAuthSuccess(user, tokens);
      },
      error: (error) => {
        if (environment.production) {
          console.error('Error al obtener tokens de Firebase:', error instanceof Error ? error.message : String(error));
        } else {
          console.error('Error al obtener tokens de Firebase:', error);
        }
      }
    });
  }

  /**
   * Inicia sesión con credenciales
   * @param email Email del usuario (o username)
   * @param password Contraseña
   * @returns Observable con el resultado del login
   */
  login(email: string, password: string): Observable<IUser> {
    this.setLoading(true);
    
    // Usar Firebase para autenticación real
    return this.loginWithFirebase(email, password);
  }

  /**
   * Login con Firebase
   */
  private loginWithFirebase(email: string, password: string): Observable<IUser> {
    return this.firebaseAuthService.loginWithEmail(email, password).pipe(
      switchMap((userCredential) => this.completeLoginFlow(userCredential.user)),
      tap(({ user, tokens }) => {
        this.handleAuthSuccess(user, tokens);
        this.navigateAfterLogin(user);
      }),
      map(response => response.user),
      catchError((error) => this.handleFirebaseError(error))
    );
  }

  /**
   * Inicia sesión con Google usando Firebase
   * @returns Observable con el resultado del login
   */
  loginWithGoogle(): Observable<IUser> {
    this.setLoading(true);
    
    return this.firebaseAuthService.loginWithGoogle().pipe(
      switchMap((userCredential) => {
        const additionalInfo = this.firebaseAuthService.getAdditionalUserInfo(userCredential);
        if (additionalInfo?.isNewUser) {
          return this.firebaseAuthService.logout().pipe(
            switchMap(() =>
              throwError(() => ({
                code: 'auth/login-new-user',
                message: 'Tu cuenta de Google no está vinculada aún. Por favor, regístrate antes de iniciar sesión con Google.'
              }))
            )
          );
        }

        return this.completeLoginFlow(userCredential.user);
      }),
      tap(({ user, tokens }) => {
        this.handleAuthSuccess(user, tokens);
        this.navigateAfterLogin(user);
      }),
      map(response => response.user),
      catchError((error) => this.handleFirebaseError(error))
    );
  }

  /**
   * Obtiene datos iniciales desde Google sin completar el login
   */
  prefillUserWithGoogle(): Observable<{ user: IUser; isNewUser: boolean }> {
    this.skipNextFirebaseAuthSync = true;

    return this.firebaseAuthService.loginWithGoogle().pipe(
      switchMap((userCredential) => {
        const additionalInfo = this.firebaseAuthService.getAdditionalUserInfo(userCredential);
        const firebaseUser = userCredential.user;
        const user = this.firebaseAuthService.mapFirebaseUserToAppUser(firebaseUser);
        const credential = this.firebaseAuthService.getCredentialFromUserCredential(userCredential);

        if (!credential) {
          return this.firebaseAuthService.logout().pipe(
            switchMap(() =>
              throwError(() => ({
                code: 'auth/missing-google-credential',
                message: 'No pudimos obtener las credenciales de Google. Intenta nuevamente.'
              }))
            )
          );
        }

        this.pendingGoogleCredential = credential;
        this.pendingGoogleUser = user;

        const cleanup$ = additionalInfo?.isNewUser
          ? from(firebaseUser.delete()).pipe(catchError(() => of(null)))
          : of(null);

        return cleanup$.pipe(
          switchMap(() => this.firebaseAuthService.logout()),
          map(() => ({
            user,
            isNewUser: !!additionalInfo?.isNewUser
          }))
        );
      }),
      catchError((error) => {
        this.clearPendingGooglePrefill();
        this.skipNextFirebaseAuthSync = false;
        return this.handleFirebaseError(error);
      }),
      finalize(() => {
        this.skipNextFirebaseAuthSync = false;
      })
    );
  }

  registerUserWithGoogle(payload: RegistrationPayload): Observable<RegisterResponse> {
    if (!this.pendingGoogleCredential) {
      return throwError(() => new Error('Debes conectar tu cuenta de Google antes de completar el registro.'));
    }

    this.setLoading(true);
    this.skipNextFirebaseAuthSync = true;

    return this.firebaseAuthService.signInWithCredential(this.pendingGoogleCredential).pipe(
      switchMap((userCredential) => {
        const firebaseUser = userCredential.user;
        const request = this.buildRegisterRequest(
          { ...payload, provider: 'google' },
          firebaseUser.uid,
          firebaseUser.emailVerified,
          { loginProvider: 'google' }
        );

        return this.authApiService.register(request).pipe(
          map((response: ApiResponse<RegisterResponse>) => {
            if (!response?.exito) {
              throw new Error(response?.mensaje || 'No se pudo registrar el usuario.');
            }

            const data: RegisterResponse = {
              ...response.data,
              mensaje: response.data?.mensaje || response.mensaje
            };

            return { firebaseUser, data };
          }),
          catchError(error => this.handleRegistrationBackendError(error, firebaseUser))
        );
      }),
      switchMap(({ firebaseUser, data }) =>
        this.firebaseAuthService.logout().pipe(
          catchError(() => of(void 0)),
          map(() => data)
        )
      ),
      finalize(() => {
        this.setLoading(false);
        this.clearPendingGooglePrefill();
        this.skipNextFirebaseAuthSync = false;
      })
    );
  }

  /**
   * Registra un usuario en Firebase y lo persiste en el backend.
   * Primero valida en el backend (email, teléfono, documento); solo si todo es válido
   * se crea el usuario en Firebase y luego en la base de datos.
   */
  registerUser(payload: RegistrationPayload): Observable<RegisterResponse> {
    if (!payload.password) {
      return throwError(() => new Error('La contraseña es requerida para registrar con email y contraseña.'));
    }

    const displayName = `${payload.nombres} ${payload.apellidos}`.trim();
    this.setLoading(true);
    this.skipNextFirebaseAuthSync = true;

    const validateRequest = this.buildValidateRegisterRequest(payload);

    return this.authApiService.validateRegister(validateRequest).pipe(
      switchMap(() => this.firebaseAuthService.registerWithEmail(payload.email, payload.password!, displayName)),
      switchMap(userCredential => {
        const firebaseUser = userCredential.user;
        
        // Intentar enviar el correo de verificación, pero no fallar si no se puede
        return this.firebaseAuthService.sendVerificationEmail().pipe(
          catchError(error => {
            if (environment.production) {
              console.warn('Error al enviar correo de verificación:', error instanceof Error ? error.message : String(error));
            } else {
              console.warn('Error al enviar correo de verificación:', error);
            }
            return of(null); // Continuar con el flujo aunque falle el envío
          }),
          map(() => userCredential)
        );
      }),
      switchMap(userCredential => {
        const firebaseUser = userCredential.user;
        const request = this.buildRegisterRequest(payload, firebaseUser.uid, firebaseUser.emailVerified);

        return this.authApiService.register(request).pipe(
          map((response: ApiResponse<RegisterResponse>) => {
            if (!response?.exito) {
              throw new Error(response?.mensaje || 'No se pudo registrar el usuario.');
            }

            const data: RegisterResponse = {
              ...response.data,
              mensaje: response.data?.mensaje || response.mensaje
            };

            return { firebaseUser, data };
          }),
          catchError(error => {
            // Si falla el registro en el backend, no eliminar el usuario de Firebase
            // Solo mostrar el error
            this.setError(error?.message || 'Error al registrar en el backend');
            return throwError(() => error);
          })
        );
      }),
      // Importante: cerrar sesión en Firebase tras el registro para que
      // el usuario no quede autenticado automáticamente.
      switchMap(({ firebaseUser, data }) => {
        return this.firebaseAuthService.logout().pipe(
          catchError(() => of(void 0)),
          map(() => data)
        );
      }),
      finalize(() => {
        this.setLoading(false);
        this.skipNextFirebaseAuthSync = false;
      })
    );
  }

  private buildValidateRegisterRequest(payload: RegistrationPayload): ValidateRegisterRequest {
    const request: ValidateRegisterRequest = {
      email: payload.email,
      telefono: payload.phoneNumber,
      userType: payload.userType,
      nombres: payload.nombres,
      apellidos: payload.apellidos,
      tipoDocumento: payload.tipoDocumento,
      numeroDocumento: payload.documentoIdentidad
    };
    if (payload.userType === UserType.ABOGADO && payload.selectedPlan) {
      const planCodigo = this.mapPlanIdToCode(payload.selectedPlan);
      if (planCodigo) {
        request.abogado = { planCodigo };
      }
    }
    return request;
  }

  private buildRegisterRequest(
    payload: RegistrationPayload,
    providerUid: string,
    emailVerified: boolean,
    extraMetadata?: Record<string, any>
  ): RegisterRequest {
    const request: RegisterRequest = {
      provider: payload.provider ?? 'firebase',
      providerUid,
      email: payload.email,
      emailVerificado: emailVerified,
      telefono: payload.phoneNumber,
      telefonoVerificado: false,
      userType: payload.userType,
      nombres: payload.nombres,
      apellidos: payload.apellidos,
      tipoDocumento: payload.tipoDocumento,
      numeroDocumento: payload.documentoIdentidad,
      fotoUrl: payload.avatarUrl,
      datosContactoJson: JSON.stringify({
        phoneNumber: payload.phoneNumber
      }),
      metadataJson: this.mergeMetadata(payload.metadataJson, extraMetadata)
    };

    if (payload.userType === UserType.ABOGADO) {
      const planCodigo = this.mapPlanIdToCode(payload.selectedPlan);
      if (!planCodigo) {
        throw new Error('Debes seleccionar un plan válido para completar el registro.');
      }

      request.abogado = {
        colegiaturaNumero: payload.colegiaturaNumero || 'PENDIENTE',
        colegiaturaRegion: payload.colegiaturaRegion || 'PENDIENTE',
        planCodigo,
        estado: 'ACTIVO',
        estadoVerificacion: 'PENDIENTE',
        especialidades: payload.especialidades,
        direccion: payload.direccion ? [{
          ciudad: payload.direccion.ciudad,
          departamento: payload.direccion.departamento,
          direccion: payload.direccion.direccion,
          codigoPostal: payload.direccion.codigoPostal,
          latitud: payload.direccion.latitud,
          longitud: payload.direccion.longitud
        }] : undefined
      };
    }

    return request;
  }

  private handleRegistrationBackendError(error: any, firebaseUser: any): Observable<never> {
    // No eliminar el usuario de Firebase, solo manejar el error
    if (error instanceof HttpErrorResponse) {
      return this.handleError(error);
    }

    const message = error?.message || 'Ocurrió un error al registrar al usuario.';
    this.setError(message);
    return throwError(() => new Error(message));
  }

  private mergeMetadata(existingJson?: string, extra?: Record<string, any>): string | undefined {
    let base: Record<string, any> = {};

    if (existingJson) {
      try {
        base = JSON.parse(existingJson) || {};
      } catch {
        base = {};
      }
    }

    if (extra) {
      base = { ...base, ...extra };
    }

    return Object.keys(base).length > 0 ? JSON.stringify(base) : undefined;
  }

  private mapPlanIdToCode(planId?: string): string | undefined {
    if (!planId) {
      return undefined;
    }

    const planMapping: Record<string, string> = {
      basico: 'PLAN_FREE',
      profesional: 'PLAN_PRO',
      premium: 'PLAN_PREMIUM',
      PLAN_BASICO: 'PLAN_FREE',
      PLAN_PROFESIONAL: 'PLAN_PRO'
    };

    return planMapping[planId] || planId;
  }

  /**
   * Verifica el email con un código de verificación
   * @param payload Contiene email y código de verificación
   * @returns Observable con la respuesta del servidor
   */
  verifyEmail(payload: { email: string; codigo: string }): Observable<any> {
    return this.http.post(`${this.apiUrl}/verify-email`, payload).pipe(
      catchError(this.handleError.bind(this))
    );
  }

  /**
   * Envía un email para restablecer la contraseña
   * @param email Email del usuario
   * @returns Observable que se completa cuando se envía el email
   */
  resetPassword(email: string): Observable<void> {
    this.setLoading(true);
    
    return this.firebaseAuthService.sendPasswordResetEmail(email).pipe(
      tap(() => {
        this.setLoading(false);
      }),
      catchError((error) => {
        return this.handleFirebaseError(error);
      })
    );
  }

  /**
   * Establece un error en el estado de autenticación
   */
  private setError(error: string | null): void {
    this.authStateSubject.next({
      ...this.authStateSubject.value,
      error,
      isLoading: false
    });
  }

  /**
   * Cierra la sesión del usuario
   */
  logout(navigateToHome: boolean = false): void {
    // Cerrar sesión en Firebase
    this.firebaseAuthService.logout().pipe(
      tap(() => {
        this.handleLogout(navigateToHome);
      }),
      catchError((error) => {
        if (environment.production) {
          console.error('Error al cerrar sesión en Firebase:', error instanceof Error ? error.message : String(error));
        } else {
          console.error('Error al cerrar sesión en Firebase:', error);
        }
        this.handleLogout(navigateToHome);
        return of(null);
      })
    ).subscribe();
  }

  /**
   * Maneja el proceso de logout localmente
   * @param navigateToHome Si es true, navega al inicio en lugar del login
   */
  private handleLogout(navigateToHome: boolean = false): void {
    clearAuthData();
    this.authStateSubject.next(this.initialState);
    
    // Sincronizar logout con SessionService
    if (this.sessionService) {
      this.sessionService.logout();
    }
    
    // Navegar según el parámetro: inicio o login (por defecto login para interceptor)
    if (navigateToHome) {
      this.router.navigate(['/'], { replaceUrl: true });
    } else {
      this.router.navigate(['/auth/login']);
    }
  }

  /**
   * Configura la sincronización entre AuthService y SessionService
   */
  private setupSessionSync(): void {
    // Escuchar cambios en el estado de autenticación y sincronizar con SessionService
    // Solo sincronizar cuando hay cambios reales para evitar loops
    let lastSyncState: boolean | null = null;
    
    this.authState$.subscribe(state => {
      const currentAuthState = state.isAuthenticated;
      
      // Solo sincronizar si el estado cambió
      if (lastSyncState !== currentAuthState) {
        lastSyncState = currentAuthState;
        
        if (state.isAuthenticated && state.user) {
          // Usuario autenticado - sincronizar
          this.syncWithSessionService(state.user);
        } else if (!state.isAuthenticated) {
          // Usuario no autenticado - cerrar sesión en SessionService
          if (this.sessionService && this.sessionService.getCurrentSession().isAuthenticated) {
            this.sessionService.logout();
          }
        }
      }
    });
  }

  /**
   * Refresca el token de acceso usando el refresh token
   * @returns Observable con los nuevos tokens
   */
  refreshToken(): Observable<IAuthTokens> {
    const firebaseUser = this.firebaseAuthService.getCurrentUser();

    if (firebaseUser) {
      return this.completeLoginFlow(firebaseUser).pipe(
        tap(({ user, tokens }) => {
          this.handleAuthSuccess(user, tokens);
        }),
        map(({ tokens }) => tokens),
        catchError(error => {
          this.handleLogout();
          return throwError(() => error);
        })
      );
    }

    const refreshToken = getAuthTokens()?.refreshToken;
    if (!refreshToken) {
      return throwError(() => new Error('No hay refresh token disponible'));
    }

    return this.http.post<{ tokens: IAuthTokens }>(`${this.apiUrl}/refresh`, { refreshToken }).pipe(
      tap(response => {
        const { tokens } = response;
        saveAuthTokens(tokens);
        this.authStateSubject.next({
          ...this.authStateSubject.value,
          tokens
        });
      }),
      map(response => response.tokens),
      catchError(error => {
        this.handleLogout();
        return throwError(() => error);
      })
    );
  }

  /**
   * Obtiene el perfil del usuario actual
   * @returns Observable con los datos del usuario
   */
  getUserProfile(): Observable<IUser> {
    return this.http.get<IUser>(`${this.apiUrl}/profile`)
      .pipe(
        tap(user => {
          saveUserData(user);
          this.authStateSubject.next({
            ...this.authStateSubject.value,
            user
          });
        }),
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Verifica si el usuario tiene un rol específico
   * @param role Rol a verificar
   * @returns true si el usuario tiene el rol, false en caso contrario
   */
  hasRole(role: string): boolean {
    const user = this.authStateSubject.value.user;
    return user?.roles?.includes(role) || false;
  }

  /**
   * Verifica si el usuario tiene un permiso específico
   * @param permission Permiso a verificar
   * @returns true si el usuario tiene el permiso, false en caso contrario
   */
  hasPermission(permission: string): boolean {
    const user = this.authStateSubject.value.user;
    return user?.permissions?.includes(permission) || false;
  }

  /**
   * Establece la URL a la que redirigir después del login
   * @param url URL a la que redirigir
   */
  setRedirectUrl(url: string): void {
    this.redirectUrl = url;
  }

  /**
   * Navega a la URL guardada después del login o a la ruta por defecto
   */
  private navigateAfterLogin(user: IUser): void {
    // No redirigir si estamos en páginas de acción de Firebase (verify email, reset password)
    const currentUrl = this.router.url;
    if (currentUrl.includes('/auth/action') || currentUrl.includes('/auth/verify-email')) {
      return;
    }

    const userType = user.userType ?? UserType.CLIENTE;
    const defaultRoute = userType === UserType.ABOGADO ? '/abogado/dashboard' : '/cliente/dashboard';
    const url = this.redirectUrl || defaultRoute;
    this.redirectUrl = null;

    // Verificar si necesita verificación de email
    const firebaseUser = this.firebaseAuthService.getCurrentUser();
    if (firebaseUser && !firebaseUser.emailVerified && firebaseUser.providerData[0]?.providerId !== 'google.com') {
      this.router.navigate(['/auth/verify-email'], {
        queryParams: { email: firebaseUser.email }
      });
      return;
    }

    this.router.navigateByUrl(url);
  }

  /**
   * Maneja el éxito de autenticación
   * @param user Datos del usuario
   * @param tokens Tokens de autenticación
   */
  private handleAuthSuccess(user: IUser, tokens: IAuthTokens): void {
    
    
    saveAuthTokens(tokens);
    saveUserData(user);
    
    
    
    // Verificar qué se guardó realmente
    const savedUser = getUserData();
    
    
    if (user.profileToken) {
    
      saveProfileToken(user.profileToken);
    }

    this.authStateSubject.next({
      user,
      tokens,
      isAuthenticated: true,
      isLoading: false,
      error: null
    });

    const profile = user.profile ?? this.buildFallbackProfile(user, user.userType ?? UserType.CLIENTE);
    
    
    
    const sessionUser: SessionUser = {
      id: user.id,
      email: user.email,
      userType: user.userType ?? UserType.CLIENTE,
      profile: profile,
      userRoles: (user.roles || profile?.availableRoles || []) as UserType[]
    };

    

    this.sessionService.login(sessionUser);
  }

  /**
   * Sincroniza el estado de autenticación con SessionService
   * Esto permite que el header detecte correctamente cuando el usuario está autenticado
   */
  private syncWithSessionService(user: IUser): void {
    if (!this.sessionService) {
      return;
    }

    const userType = user.userType ?? UserType.CLIENTE;
    const profile = user.profile ?? this.buildFallbackProfile(user, userType);

    

    const sessionUser: SessionUser = {
      id: user.id,
      email: user.email,
      userType,
      profile,
      userRoles: (user.roles || profile?.availableRoles || []) as UserType[]
    };

    

    this.sessionService.login(sessionUser);
  }

  /**
   * Valida y normaliza una URL de avatar
   * Retorna el avatar por defecto si la URL es inválida, vacía o null
   */
  private validateAvatarUrl(url: string | null | undefined): string {
    const defaultAvatar = 'assets/images/default-avatar.png';
    
    if (!url || typeof url !== 'string') {
      return defaultAvatar;
    }
    
    const trimmedUrl = url.trim();
    
    // Si está vacía, es 'null' o 'undefined' como string, usar el avatar por defecto
    if (trimmedUrl === '' || trimmedUrl === 'null' || trimmedUrl === 'undefined') {
      return defaultAvatar;
    }
    
    // Validar que sea una URL válida (debe empezar con http, https, o assets)
    if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://') || trimmedUrl.startsWith('assets/')) {
      return trimmedUrl;
    }
    
    // Si no es una URL válida, usar el avatar por defecto
    return defaultAvatar;
  }

  private buildFallbackProfile(user: IUser, userType: UserType): Profile {
    const fullName = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
    return {
      id: user.id,
      user_id: user.id,
      full_name: fullName || user.email,
      email: user.email,
      phone: undefined,
      telefono: undefined,
      avatar_url: this.validateAvatarUrl(user.avatar),
      bio: '',
      location: '',
      direccion: '',
      date_of_birth: undefined,
      gender: undefined,
      is_lawyer: userType === UserType.ABOGADO,
      is_verified: true,
      created_at: new Date(),
      updated_at: new Date(),
      document_type: undefined,
      document_number: undefined
    };
  }

  /**
   * Establece el estado de carga
   * @param isLoading Estado de carga
   */
  private setLoading(isLoading: boolean): void {
    this.authStateSubject.next({
      ...this.authStateSubject.value,
      isLoading,
      error: isLoading ? null : this.authStateSubject.value.error
    });
  }

  resetGooglePrefill(): void {
    this.clearPendingGooglePrefill();
  }

  private clearPendingGooglePrefill(): void {
    this.pendingGoogleCredential = null;
    this.pendingGoogleUser = null;
  }

  private ensureGoogleSessionForCompletion(): Observable<any> {
    const firebaseUser = this.firebaseAuthService.getCurrentUser();

    if (firebaseUser) {
      return of(firebaseUser);
    }

    if (!this.pendingGoogleCredential) {
      return throwError(() => ({
        code: 'auth/google-prefill-required',
        message: 'Debes conectar tu cuenta de Google antes de completar el registro.'
      }));
    }

    return this.firebaseAuthService.signInWithCredential(this.pendingGoogleCredential).pipe(
      map((userCredential) => userCredential.user)
    );
  }

  private completeLoginFlow(firebaseUser: User): Observable<{ user: IUser; tokens: IAuthTokens }> {
    return this.firebaseAuthService.createAuthTokens(firebaseUser).pipe(
      switchMap((tokens) => this.syncSessionWithBackend(firebaseUser, tokens))
    );
  }

  private syncSessionWithBackend(firebaseUser: User, tokens: IAuthTokens): Observable<{ user: IUser; tokens: IAuthTokens }> {
    const requestPayload: AuthSessionRequest = {
      providerUid: firebaseUser.uid,
      email: firebaseUser.email ?? undefined,
      firebaseIdToken: tokens.accessToken
    };

    return this.authApiService.syncSession(requestPayload).pipe(
      map((response: ApiResponse<AuthSessionResponse>) => {
        if (!response?.exito || !response.data) {
          throw new Error(response?.mensaje || 'No fue posible sincronizar la sesión con el backend.');
        }

        const mappedUser = this.mapProfileResponseToUser(firebaseUser, response.data);
        return { user: mappedUser, tokens };
      }),
      catchError((error: any) => {
        // Extraer mensaje del backend si existe
        const backendMessage = error?.error?.mensaje || error?.message || 'No fue posible sincronizar la sesión con el backend.';
        return throwError(() => new Error(backendMessage));
      })
    );
  }

  private mapProfileResponseToUser(firebaseUser: User, sessionResponse: AuthSessionResponse): IUser {
    const baseUser = this.firebaseAuthService.mapFirebaseUserToAppUser(firebaseUser);
    
    
    const profileData = sessionResponse.profile;
    

    if (!profileData) {
      const fallbackUserType = baseUser.userType ?? UserType.CLIENTE;
      return {
        ...baseUser,
        userType: fallbackUserType,
        profile: this.buildFallbackProfile(baseUser, fallbackUserType),
        profileToken: sessionResponse.profileToken
      };
    }

    const fullName = profileData.fullName || `${profileData.nombres ?? ''} ${profileData.apellidos ?? ''}`.trim();

    const createdAt = profileData?.personaCreadaEn ? new Date(profileData.personaCreadaEn) : new Date();
    const updatedAt = profileData?.personaActualizadaEn ? new Date(profileData.personaActualizadaEn) : new Date();
    const perfilCompletado = profileData?.perfilCompletado ?? false;
    const perfilProgreso = profileData?.perfilProgreso ?? (perfilCompletado ? 100 : 0);
    const estadoVerificacion = profileData?.estadoVerificacion ?? null;
    const validationStatus = this.mapEstadoVerificacion(estadoVerificacion);

    // Validar y obtener la URL del avatar
    const avatarUrl = this.validateAvatarUrl(profileData?.fotoUrl ?? baseUser.avatar);

    const profile: Profile = {
      // id: profileData?.personaId ?? baseUser.id,
      id: (profileData?.userType === UserType.ABOGADO ? profileData?.abogadoId : profileData?.clienteId) ?? baseUser.id,
      user_id: profileData?.usuarioId ?? baseUser.id,
      full_name: fullName,
      email: profileData?.email ?? baseUser.email,
      phone: profileData?.telefono,
      telefono: profileData?.telefono,
      avatar_url: avatarUrl,
      bio: undefined,
      location: undefined,
      direccion: profileData?.direccion,
      date_of_birth: undefined,
      gender: profileData?.genero,
      is_lawyer: profileData?.userType === UserType.ABOGADO,
      is_verified: profileData?.emailVerificado ?? false,
      created_at: createdAt,
      updated_at: updatedAt,
      document_type: profileData?.documentoTipo,
      document_number: profileData?.documentoNumero,
      profile_completed: perfilCompletado,
      profile_progress: perfilProgreso,
      validation_status: validationStatus,
      estado_verificacion: estadoVerificacion || undefined
    };

    // Determinar roles disponibles
    // El backend ahora envía 'roles' en lugar de 'availableRoles'
    let availableRoles: UserType[] = [];
    
    
    
    if (profileData?.roles && profileData.roles.length > 0) {
      // Si el backend proporciona roles, usarlos directamente
      availableRoles = profileData.roles;
      
    } else {
      // Fallback: determinar roles basándose en clienteId y abogadoId
      
      if (profileData?.clienteId) {
        availableRoles.push(UserType.CLIENTE);
      
      }
      if (profileData?.abogadoId) {
        availableRoles.push(UserType.ABOGADO);
      
      }
    }
    
    

    // Guardar availableRoles en el profile para acceso posterior
    profile.availableRoles = availableRoles;

    
    
    // IMPORTANTE: Priorizar activeRole del backend si existe
    // activeRole indica el rol actualmente activo del usuario
    // Si no existe, usar userType como fallback
    let finalUserType: UserType;
    if (profileData?.activeRole) {
      finalUserType = profileData.activeRole;
    
    } else if (profileData?.userType) {
      finalUserType = profileData.userType;
    
    } else if (baseUser.userType) {
      finalUserType = baseUser.userType;
    
    } else {
      finalUserType = UserType.CLIENTE;
    
    }
    
    

    const mappedUser: IUser = {
      ...baseUser,
      id: profileData?.usuarioId ?? baseUser.id,
      email: profileData?.email ?? baseUser.email,
      firstName: profileData?.nombres ?? baseUser.firstName,
      lastName: profileData?.apellidos ?? baseUser.lastName,
      avatar: profile.avatar_url,
      userType: finalUserType,
      roles: availableRoles as string[],
      profile,
      profileToken: sessionResponse.profileToken
    };

    

    return mappedUser;
  }

  private mapEstadoVerificacion(estado: string | null | undefined): 'pending' | 'validated' | 'rejected' | undefined {
    if (!estado) {
      return undefined;
    }
    const normalized = estado.trim().toUpperCase();
    if (normalized.includes('VALIDA')) {
      return 'validated';
    }
    if (normalized.includes('RECHAZ')) {
      return 'rejected';
    }
    return 'pending';
  }

  /**
   * Maneja errores de autenticación
   * @param error Error HTTP
   * @returns Observable con el error
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'Ha ocurrido un error en la autenticación';

    const backendMessage = this.extractBackendErrorMessage(error);
    if (backendMessage) {
      errorMessage = backendMessage;
    } else if (error.error instanceof ErrorEvent) {
      errorMessage = `Error: ${error.error.message}`;
    } else {
      switch (error.status) {
        case 400:
          errorMessage = 'Solicitud inválida. Verifica los datos ingresados.';
          break;
        case 401:
          errorMessage = 'Credenciales incorrectas';
          break;
        case 403:
          errorMessage = 'No tiene permisos para acceder';
          break;
        case 404:
          errorMessage = 'Servicio no encontrado';
          break;
        case 409:
          errorMessage = 'Conflicto al procesar la solicitud.';
          break;
        case 500:
          errorMessage = 'Error interno del servidor';
          break;
      }
    }

    this.authStateSubject.next({
      ...this.authStateSubject.value,
      isLoading: false,
      error: errorMessage
    });

    return throwError(() => new Error(errorMessage));
  }

  private extractBackendErrorMessage(error: HttpErrorResponse): string | null {
    const payload = error?.error;

    if (!payload) {
      return null;
    }

    if (typeof payload === 'string') {
      try {
        const parsed = JSON.parse(payload);
        return parsed?.message || parsed?.mensaje || null;
      } catch {
        return payload;
      }
    }

    if (typeof payload === 'object') {
      return payload.message || payload.mensaje || null;
    }

    return null;
  }

  /**
   * Maneja errores de Firebase
   * @param error Error de Firebase
   * @returns Observable con el error
   */
  private handleFirebaseError(error: any): Observable<never> {
    let errorMessage = 'Ha ocurrido un error en la autenticación';
    
    // Códigos de error comunes de Firebase Auth
    const errorCode = error?.code || '';
    const errorMessageText = error?.message || '';
    
    // Mapeo completo de códigos de error de Firebase a mensajes en español
    const errorMap: { [key: string]: string } = {
      // Errores de autenticación
      'auth/invalid-credential': 'Las credenciales ingresadas no son correctas. Por favor, verifica tu email y contraseña.',
      'auth/user-not-found': 'No existe una cuenta registrada con este email.',
      'auth/wrong-password': 'La contraseña ingresada es incorrecta. Por favor, verifica tu contraseña.',
      'auth/invalid-email': 'El formato del email no es válido. Por favor, ingresa un email correcto.',
      'auth/user-disabled': 'Esta cuenta ha sido deshabilitada. Por favor, contacta con el soporte.',
      'auth/too-many-requests': 'Demasiados intentos fallidos. Por favor, espera unos minutos antes de intentar nuevamente.',
      'auth/operation-not-allowed': 'Este método de autenticación no está habilitado. Por favor, contacta con el administrador.',
      'auth/weak-password': 'La contraseña es muy débil. Debe tener al menos 6 caracteres.',
      'auth/email-already-in-use': 'Ya existe una cuenta registrada con este email. Si es tu cuenta, intenta iniciar sesión.',
      'auth/requires-recent-login': 'Por seguridad, necesitas iniciar sesión nuevamente para realizar esta acción.',
      'auth/credential-already-in-use': 'Estas credenciales ya están asociadas con otra cuenta.',
      'auth/invalid-verification-code': 'El código de verificación ingresado no es válido.',
      'auth/invalid-verification-id': 'El ID de verificación no es válido. Por favor, solicita un nuevo código.',
      'auth/code-expired': 'El código de verificación ha expirado. Por favor, solicita uno nuevo.',
      'auth/session-cookie-expired': 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.',
      
      // Errores de red
      'auth/network-request-failed': 'Error de conexión a internet. Por favor, verifica tu conexión e intenta nuevamente.',
      
      // Errores de proveedores OAuth
      'auth/popup-closed-by-user': 'La ventana de autenticación fue cerrada antes de completar el proceso.',
      'auth/popup-blocked': 'La ventana emergente fue bloqueada por el navegador. Por favor, permite ventanas emergentes e intenta nuevamente.',
      'auth/account-exists-with-different-credential': 'Ya existe una cuenta con este email usando otro método de autenticación.',
      'auth/auth-domain-config-required': 'Error de configuración. Por favor, contacta con el soporte.',
      
      // Errores de cuota
      'auth/quota-exceeded': 'Se ha excedido la cuota de solicitudes. Por favor, intenta más tarde.',
      'auth/login-new-user': 'Tu cuenta de Google no está vinculada aún. Por favor, regístrate antes de iniciar sesión con Google.',
      'auth/missing-google-credential': 'No pudimos conectarnos con tu cuenta de Google. Intenta nuevamente.',
      'auth/google-prefill-required': 'Debes conectar tu cuenta de Google antes de completar el registro.',
      
      // Errores de captcha
      'auth/captcha-check-failed': 'La verificación de seguridad falló. Por favor, intenta nuevamente.',
      'auth/invalid-app-credential': 'Credenciales de la aplicación no válidas. Por favor, contacta con el soporte.',
      
      // Errores generales
      'auth/app-not-authorized': 'Esta aplicación no está autorizada. Por favor, contacta con el administrador.',
      'auth/app-not-installed': 'La aplicación no está instalada correctamente.',
      'auth/argument-error': 'Error en los argumentos proporcionados.',
      'auth/invalid-api-key': 'Error de configuración. Por favor, contacta con el soporte.',
      'auth/invalid-app-id': 'Error de configuración. Por favor, contacta con el soporte.',
      'auth/invalid-user-token': 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.',
      'auth/missing-android-pkg-name': 'Error de configuración.',
      'auth/missing-continue-uri': 'Error de configuración.',
      'auth/missing-ios-bundle-id': 'Error de configuración.',
      'auth/unauthorized-domain': 'Este dominio no está autorizado para esta aplicación.',
    };
    
    // Buscar el error por código
    if (errorCode && errorMap[errorCode]) {
      errorMessage = errorMap[errorCode];
    } else if (errorMessageText) {
      // Si no hay código, buscar en el mensaje
      const messageLower = errorMessageText.toLowerCase();
      
      if (messageLower.includes('invalid') && messageLower.includes('credential')) {
        errorMessage = 'Las credenciales ingresadas no son correctas. Por favor, verifica tu email y contraseña.';
      } else if (messageLower.includes('invalid_login_credentials')) {
        errorMessage = 'Las credenciales ingresadas no son correctas. Por favor, verifica tu email y contraseña.';
      } else if (messageLower.includes('wrong-password') || messageLower.includes('wrong password')) {
        errorMessage = 'La contraseña ingresada es incorrecta. Por favor, verifica tu contraseña.';
      } else if (messageLower.includes('user-not-found') || messageLower.includes('user not found')) {
        errorMessage = 'No existe una cuenta registrada con este email.';
      } else if (messageLower.includes('email-already-in-use') || messageLower.includes('email already in use')) {
        errorMessage = 'Ya existe una cuenta registrada con este email. Si es tu cuenta, intenta iniciar sesión.';
      } else if (messageLower.includes('network') || messageLower.includes('connection')) {
        errorMessage = 'Error de conexión a internet. Por favor, verifica tu conexión e intenta nuevamente.';
      } else if (messageLower.includes('too-many-requests') || messageLower.includes('too many requests')) {
        errorMessage = 'Demasiados intentos fallidos. Por favor, espera unos minutos antes de intentar nuevamente.';
      } else {
        // Si el mensaje viene del backend, usarlo directamente
        errorMessage = errorMessageText;
      }
    } else {
      // Mensaje por defecto
      errorMessage = 'Error al autenticarse. Por favor, verifica tus credenciales e intenta nuevamente.';
    }
    
    this.authStateSubject.next({
      ...this.authStateSubject.value,
      isLoading: false,
      error: errorMessage
    });
    
    return throwError(() => new Error(errorMessage));
  }

  /**
   * Cambia el rol activo del usuario (VERSIÓN SIMPLIFICADA - REUTILIZA LÓGICA EXISTENTE)
   * @param newRole Nuevo rol a activar
   * @returns Observable con la respuesta
   * 
   * Mejoras sobre la versión original:
   * - Reutiliza mapProfileResponseToUser en lugar de duplicar lógica
   * - Reutiliza handleAuthSuccess para guardado consistente
   * - Elimina verificación post-guardado redundante
   * - Reduce de ~170 líneas a ~50 líneas
   * - Más mantenible: cambios en mapeo solo en un lugar
   */
  changeActiveRoleV2(newRole: UserType): Observable<void> {
    this.setLoading(true);
    
    const profileToken = getProfileToken();
    if (!profileToken) {
      this.setLoading(false);
      return throwError(() => new Error('No hay token de sesión disponible'));
    }
    
    const request: import('../../models/auth.model').ChangeActiveRoleRequest = {
      activeRole: newRole
    };

    return this.http.put<import('../../models/auth.model').ApiResponse<import('../../models/auth.model').ChangeActiveRoleResponse>>(
      `${environment.apiUrl}/auth/active-role`,
      request
    ).pipe(
      tap((response) => {
        if (!response?.exito || !response.data) {
          throw new Error(response?.mensaje || 'No fue posible cambiar el rol activo.');
        }

        const { profileToken: newProfileToken, profile } = response.data;
        
        saveProfileToken(newProfileToken);

        const firebaseUser = this.firebaseAuthService.getCurrentUser();
        if (!firebaseUser) {
          throw new Error('No hay sesión de Firebase activa');
        }

        const sessionResponse: AuthSessionResponse = {
          profileToken: newProfileToken,
          expiresAt: response.data.expiresAt,
          expiresInSeconds: response.data.expiresInSeconds,
          profile: profile
        };
        
        const updatedUser = this.mapProfileResponseToUser(firebaseUser, sessionResponse);
        const currentTokens = getAuthTokens();
        
        if (currentTokens) {
          this.handleAuthSuccess(updatedUser, currentTokens);
        }
      }),
      map(() => void 0),
      catchError((error) => {
        this.setLoading(false);
        return this.handleError(error);
      }),
      finalize(() => this.setLoading(false))
    );
  }

  /**
   * Obtiene el token de acceso actual
   * @returns Token de acceso o null
   */
  getAccessToken(): string | null {
    return getAccessToken();
  }
}
